import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDebounceEffect, useMount } from 'ahooks'
import { Button, Col, ConfigProvider, Layout, message, Modal, notification, Row } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import zhCN from 'antd/lib/locale/zh_CN'
import styles from './index.module.less'
import {cloneDeep, isArray, isEmpty} from 'lodash-es'

import { Provider } from '@/common/commonContext'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { getTenantId, getUser, navigatePage, onLogout } from '@/common/utils'
import microApp from '@micro-zoe/micro-app'
import useUser from '@/hooks/useUser'
import { abortRequest } from '@/api'
import waterMark from '@/components/WaterMark'
import useGlobalStore from '@/store'
import { useShallow } from 'zustand/react/shallow'
import useChatStore from '@/store/chatStore.ts'
import { useAliveController } from 'react-activation'
import useWorkshopStore from '@/store/workshop.ts'
import { globalConfig, setGlobalConfig } from '@/utils/data.ts'
import { getUCenterAppList } from '@/services'
import MenuSide from '@/layouts/MenuSide'
import classNames from 'classnames'
import queryParse from '@/utils/queryParse.ts'
import useWorkOrderStore, { WORK_ORDER_ENUMS } from '@/store/workOrder.ts'
import { authDict } from 'shj-utils'
import authConfig from '@/config/authConfig'
import { getUserIdentities } from '@/services/user.ts'
import {SConfigProvider} from "shj-components";
import {batchSave, cacheQuery} from "@/services/cache.ts";
import {useAtom} from "jotai";
import {callDataAtom, callStateAtom} from "@/pages/callTool/intelligentOutboundCall/components/Info/store";

const Layouts = forwardRef(() => {
  const navigate = useNavigate()
  const { getCachingNodes, refreshScope } = useAliveController()
  const { getWebConfig, getMenu, getProfile, isPlatform } = useUser()
  const mountedRef: any = useRef(false)
  const reloadCacheRef: any = useRef('')

  const location = useLocation()
  const theme = useGlobalStore((state: any) => state.theme)
  const [currentThem, setCurrentThem] = useState(theme)
  const [modal, modalHolder] = Modal.useModal()
  const [messageApi, messageHolder] = message.useMessage()

  const {
    updateSelectedApp,
    globalUser,
    menuCollapsed,
    menuCollapseAnimating,
    setIdentities,
    getCurrentIdentity,
    useInfo,
    setUserInfo,
    tipsVisible,
    setTipsVisible,
  } = useGlobalStore(
    useShallow((state: any) => ({
      updateSelectedApp: state.updateSelectedApp,
      globalUser: state.user,
      menuCollapsed: state.menuCollapsed,
      menuCollapseAnimating: state.menuCollapseAnimating,
      setIdentities: state.setIdentities,
      getCurrentIdentity: state.getCurrentIdentity,
      useInfo: state.useInfo,
      setUserInfo: state.setUserInfo,
      tipsVisible: state.tipsVisible,
      setTipsVisible: state.setTipsVisible,
    }))
  )
  const { fetchNav, resetWorkshop, setAppList, setCaasMenu, setAllAppList, setAppLoading } =
    useWorkshopStore(
      useShallow((state: any) => ({
        fetchNav: state.fetchNav,
        resetWorkshop: state.reset,
        setAppList: state.setAppList,
        setCaasMenu: state.setCaasMenu,
        setAllAppList: state.setAllAppList,
        setAppLoading: state.setAppLoading,
      }))
    )
  const {
    selectWorkOrderApp,
    workOrderStoreData,
    setWorkOrderExpandKeys,
    routeOfPath,
    routeNextItem,
    formatWorkOrderSearch,
    reloadWorkOrderData,
    reloadWorkOrderDataAsync,
  } = useWorkOrderStore(
    useShallow((state: any) => ({
      selectWorkOrderApp: state.selectWorkOrderApp,
      setWorkOrderExpandKeys: state.setWorkOrderExpandKeys,
      workOrderStoreData: state.data,
      routeOfPath: state.routeOfPath,
      routeNextItem: state.routeNextItem,
      formatWorkOrderSearch: state.formatWorkOrderSearch,
      reloadWorkOrderData: state.reloadWorkOrderData,
      reloadWorkOrderDataAsync: state.reloadWorkOrderDataAsync,
    }))
  )

  const user = globalUser || getUser() || {}

  const [state, setState]: any = useState({
    user: {},
    menus: [],
    appSelectedKeys: [],
  })

  const handleCache = () => {
    /**
     * 处理缓存逻辑
     * 遗留问题：
     * 1、 navigate(-1)等于浏览器的回退，会保留原有状态
     * 2、 子应用缓存（需单独在子应用处理）
     *
     * 现有能力：
     * 1、 sBack组件默认返回不会清除组件缓存(可以通过传入自定义方法触发清除)
     * 2、 navigate跳转带上saveCache不会清除缓存
     * 3、 KeepAlive节点id带上keep会一直保留组件缓存，不会清除（适合常驻缓存场景）
     * 除上述三种情况、其它情况皆会默认刷新缓存（navigate(-1)会取到上个页面状态值，是否清除会不定，可以通过操作sessionStorage解决）
     */
    const cacheNodes = getCachingNodes()
    const hasCacheList = cacheNodes?.filter(item => item.name === location.pathname)
    const isBack = sessionStorage.getItem('navigateBack')
    if (hasCacheList?.length && hasCacheList?.[0]?.getInstance()?.id?.includes('keep')) {
      return
    }
    // 重复渲染太多时，保证只刷新一次
    if (
      hasCacheList?.length &&
      !location?.state?.saveCache &&
      reloadCacheRef.current !== location.pathname &&
      !isBack
    ) {
      console.log(
        '清除缓存',
        location?.state?.saveCache,
        reloadCacheRef.current,
        location.pathname,
        isBack
      )
      refreshScope(location.pathname)
    }
    reloadCacheRef.current = location.pathname
    sessionStorage.setItem('navigateBack', '')
  }

  handleCache()
  const fetchTheme = useGlobalStore((state: any) => state.fetchTheme)

  function setMenuOfKey(key: string, menuData?) {
    state.menus = menuData
    state.appSelectedKeys = [key]
    setState({ ...state })
  }

  const setWaterMark = () => {
    const user = globalUser || getUser() || {}
    const container: any = document.querySelector('#mainContainer')
    const texts: string[] = []

    if (user?.currentDepartmentPosition?.companyName) {
      texts.push(user.currentDepartmentPosition.companyName)
    }

    if (user?.alias && user?.alias !== user?.workerNumber) {
      texts.push(user.alias)
    }

    if (user?.currentDepartmentPosition?.deptPosName) {
      texts.push(user.currentDepartmentPosition.deptPosName)
    }

    const waterParams = {
      container,
      content: `${user?.workerNumber}-${texts.join('-')}`,
    }

    waterMark.txtwatermark(waterParams)
  }

  useLayoutEffect(() => {
    authDict.value = authConfig
    //
    // console.log('AccountInformation', authDict['AccountInformation']) // 1  写了配置，后端也返回了
    // console.log('DeleteApplication', authDict['DeleteApplication']) // undefined  写了配置，但后端没返回

    // console.log('authDict', authDict._permission)
    getMenu().then(menuData => {
      const applications = menuData.map(d => ({ key: d.key, label: d.name }))
      setState({
        applications: applications,
      })
      setMenuOfKey(applications[0]?.key, menuData)
    })

    // authDict.register(buttonAuth)
    // getWebConfig().then(configData => {
    //   setState({
    //     logo: configData?.logo,
    //   })
    // })
    // 获取工作台 应用列表
    fetchAppList()
    fetchNav()
    // setTimeout(() => {
    //   workOrderNext()
    // }, 3000)
  }, [])

  const workOrderChangeTab = (
    tabKey: string,
    pathname = location.pathname,
    search = location.search
  ) => {
    const parsedSearch: any = queryParse(search)
    selectWorkOrderApp(tabKey, { appCode: parsedSearch.app })
    setWorkOrderExpandKeys([
      ...workOrderStoreData.workOrderExpandKeys.filter(k => k !== tabKey),
      tabKey,
    ])
    const _search = formatWorkOrderSearch({ ...parsedSearch, tab: tabKey })
    routeOfPath(pathname, { search: _search, replace: true, navigate })
  }

  const workOrderNext = () => {
    // microApp.unmountApp('flow')
    routeNextItem(navigate)
    reloadWorkOrderData()
    reloadFlowApp();
  }

  const workOrderNextAsync = (opt = {} as any) => {
    routeNextItem(navigate)
    reloadWorkOrderDataAsync(opt)
  }

  const reloadFlowApp = ()=>{
    setTimeout(() => {
      microApp.reload('flow', false).then(result => {
        if (result) {
          console.log('重新渲染成功')
        } else {
          console.log('重新渲染失败')
        }
      })
    }, 100)
  }
  
  const onBreadNavigate = data => {
    const { appPath, path, search } = data.detail || {}
    navigatePage(appPath, path, navigate, null, search)
  }

  const onClickToMainAppPage = (url) => {
    navigate(url)
  }

  useDebounceEffect(
    () => {
      microApp.setGlobalData({
        openDownload: onDownload,
        onClickToMainAppPage,
        workOrderChangeTab,
        WORK_ORDER_ENUMS,
        workOrderNext,
        workOrderNextAsync,
      })
    },
    [],
    { wait: 100 }
  )

  useDebounceEffect(
    () => {
      if (!mountedRef.current) {
        mountedRef.current = true
        // console.log('configData', configData)
        // setWaterMark()
        initData()
        // window.addEventListener('resize', setWaterMark)
      }

      try {
        const themeString = localStorage.getItem('ANTD_CONFIG_PROVIDER')
        if (themeString) {
          const themCss = JSON.parse(themeString)
          setCurrentThem(themCss)
        }
      } catch (err) {
        console.log('err', err)
      }

      setGlobalConfig?.({ modal, message: messageApi })
      window.addEventListener('BREAD_CLICK_CROSS', onBreadNavigate)
      window.addEventListener('BREAD_CLICK', onBreadNavigate)
      return () => {
        window.removeEventListener('resize', setWaterMark)
        window.removeEventListener('BREAD_CLICK_CROSS', onBreadNavigate)
        window.removeEventListener('BREAD_CLICK', onBreadNavigate)
      }
    },
    [],
    { wait: 150 }
  )

  const initData = async () => {
    const user = getUser()

    let userData = null

    if (user) {
      userData = user
    }
    // console.log('user', user, userData)
    if (!sessionStorage.getItem('PROFILE_FETCHED') || user === 'undefined' || !userData) {
      try {
        const userRes = await getProfile()

        if (userRes) {
          userData = userRes
        }
      } catch (err) {
        // console.log(err)
      }
    } else {
      sessionStorage.removeItem('PROFILE_FETCHED')
    }

    getUserIdentities().then(res => {
      setIdentities(res)
      notifyPosMessage(res)
    })

    if (userData) {
      state.user = userData
      setState(cloneDeep(state))
    }

    microApp.setGlobalData({ userInfo: userData, locale: zhCN })
  }

  useEffect(() => {
    fetchTheme()
  }, [])

  useEffect(() => {
    setCurrentThem(theme)
  }, [theme])

  //
  const notifyPosMessage = identities => {
    if (identities?.length) {
      const current = getCurrentIdentity(identities)
      const user = getUser()
      // console.log('identities current', current)

      // notification.destroy()
      // notification.success({
      //   message: `欢迎回来！${user.userName}`,
      //   duration: 6000,
      //   description: (
      //     <div>
      //       您当前登录的身份为
      //       <strong style={{ color: 'red' }}>
      //         <span>{current?.orgNamePath}</span>【{current?.roleName}】
      //       </strong>
      //       ，如需切换身份请点击左下角个人头像进行设置。
      //     </div>
      //   ),
      // })
      //
      setUserInfo({
        userName: user.userName,
        orgNamePath: current?.orgNamePath,
        roleName: current?.roleName,
      })

      if (!localStorage.getItem('tipsShow')) {
        setTipsVisible(true)
        localStorage.setItem('tipsShow', '1')
      }

      setTimeout(() => setTipsVisible(false), 15000)
    }
  }

  const fetchAppList = () => {
    setAppLoading(true)
    getUCenterAppList({
      tenantId: getTenantId(),
      // appType: 'OFFICIAL',
    })
      .then((res: any) => {
        const caasItem = res.find(d => d.app?.code === 'enterpriseBackground')
        setAppList(res.filter(d => d.app?.appType === 'OFFICIAL'))
        setCaasMenu(caasItem)
        // console.log('fetchAppList', res)
        setAllAppList(res)
      })
      .finally(() => {
        setAppLoading(false)
      })
  }

  const reset = useChatStore((state: any) => state.reset)
  const { clear } = useAliveController()
  const logout = async () => {
    return abortRequest(500).then(() => {
      navigate('/login', { replace: true })
      onLogout()
      reset()
      resetWorkshop()
      clear?.()
    })
  }

  const tabChange = menuItem => {
    // customTabsRef.current.setNavItem(path)
    // customTabsRef.current?.changeTopMenu?.(menu)
    // console.log('tabChange', menuItem)
    updateSelectedApp({ ...menuItem })
  }

  // const onAppClose = () => {
  //   navigate('/main', { replace: true })
  // }

  const onDownload = () => {
    let users = JSON.parse(localStorage.getItem('showDownloadModal') || '[]')
    let { workNumber } = getUser() || {}
    if (users.find(item => item === workNumber)) {
      let messageApi = globalConfig()?.message ?? message
      messageApi.success('文件正在生成中，可前往页面左下角下载中心导出')
      return
    }
    let modal = globalConfig()?.modal ?? Modal
    modal.confirm({
      title: '文件正在生成中,请前往页面左下角下载中心导出',
      content: '所有下载文件，需在下载中心统一生成，待生产完毕，可点击下载导出文件',
      okText: '打开下载中心',
      cancelText: '不再提示',
      closeIcon: '关闭',
      onOk: () => {
        // setState({ download: true })
        navigate('/main/download')
      },
      onCancel: () => {
        localStorage.setItem('showDownloadModal', JSON.stringify(users.concat(workNumber)))
      },
    })
  }

  const configProvider = {
    antdConfig: {
      theme
    },
    auths: authDict,
    userId: user?.userId,
    getTableCache: cacheQuery,
    saveTableCache: batchSave
  }

  return (
    <ConfigProvider locale={zhCN} theme={{ ...currentThem }}>
      <SConfigProvider {...configProvider}>
      <Provider value={state as any}>
        {modalHolder}
        {messageHolder}
        <Layout style={{ minHeight: '100vh' }}>
          <MenuSide onTabChange={tabChange} logout={logout} menuList={state?.menus} />

          {/*<AsideContent*/}
          {/*  ref={asideRef}*/}
          {/*  menuList={state?.menus}*/}
          {/*  appSelectedKeys={state?.appSelectedKeys}*/}
          {/*  onTabChange={tabChange}*/}
          {/*  logout={logout}*/}
          {/*/>*/}
          <div
            className={classNames(styles.mainWrap, {
              [styles.menuCollapsed]: menuCollapsed,
              [styles.animating]: menuCollapseAnimating,
            })}
          >
            <Outlet />
          </div>
          <div
            className={classNames(styles.loginSuccessTips, {
              [styles.visible]: tipsVisible,
            })}
          >
            <Row align="middle" justify="space-between">
              <Col>
                <h3>欢迎回来！{useInfo.userName}</h3>
              </Col>
              <Col>
                <Button
                  type="text"
                  size="small"
                  shape="circle"
                  onClick={() => setTipsVisible(false)}
                >
                  <CloseOutlined />
                </Button>
              </Col>
            </Row>
            <div>
              您当前登录的身份为
              <strong style={{ color: 'red' }}>
                <span>{useInfo.orgNamePath}</span>【{useInfo.roleName}】
              </strong>
              ，如需切换身份请点击左下角个人头像进行设置。
            </div>
          </div>
        </Layout>
      </Provider>
      </SConfigProvider>
    </ConfigProvider>
  )
})

export default Layouts
